<template>
    <div class="shipping-address">
        <span v-if="displayIncompleteAddressError" class="body-1 red--text ml-2">
            {{ incompleteAddressError | translation }}
        </span>
        <v-form ref="form" @submit.prevent>
            <v-layout row wrap my-0>
                <v-flex xs12 v-if="displayList && addresses && addresses.length" class="text-xs-right py-0">
                    <ShippingAddressRadioGroup :addresses="addresses" />
                   <v-btn
                        color="primary"
                        @click="resetForm()"
                        :round="buttonRound"
                        :depressed="buttonDepressed"
                        :large="buttonLarge"
                    >
                        {{ 'Enter another address' | translation }}
                    </v-btn>
                </v-flex>

                <v-flex xs12 class="py-0">
                    <p class="body-2 ma-0" v-if="inputLabelOutside">{{ 'Vorname*' | translation }}</p>
                    <v-text-field
                        :disabled="isDisabled"
                        v-model="firstName"
                        :rules="[rules.required, rules.firstName]"
                        :label="inputLabelOutside ? '' : $language.translate('Vorname*')"
                        :solo="inputSolo"
                        required
                        class="pt-0"
                    >
                    </v-text-field>
                </v-flex>

                <v-flex xs12 class="py-0">
                    <p class="body-2 ma-0" v-if="inputLabelOutside">{{ 'Nachname*' | translation }}</p>
                    <v-text-field
                        :disabled="isDisabled"
                        v-model="lastName"
                        :rules="[rules.required]"
                        :label="inputLabelOutside ? '' : $language.translate('Nachname*')"
                        :solo="inputSolo"
                        required
                        class="pt-0"
                    >
                    </v-text-field>
                </v-flex>

                <v-flex xs12 class="py-0">
                    <p class="body-2 ma-0" v-if="inputLabelOutside">{{ 'Company' | translation }}</p>
                    <v-text-field
                        :disabled="isDisabled"
                        v-model="company"
                        :label="inputLabelOutside ? '' : $language.translate('Company')"
                        :solo="inputSolo"
                        class="pt-0"
                    >
                    </v-text-field>
                </v-flex>

                <v-flex xs12 class="py-0">
                    <p class="body-2 ma-0" v-if="inputLabelOutside">{{ 'shipping address' | translation }}</p>
                    <vuetify-google-autocomplete
                        id="shipping_autocomplete"
                        @placechanged="onAutocompleteChanged"
                        autocomplete="off"
                        :placeholder="$language.translate('Addresse eingeben...')"
                        :disabled="isDisabled"
                        :solo="inputSolo"
                        :error-messages="addressErrors"
                        hide-no-data
                        no-filter
                        :city.sync="city"
                        :street.sync="street"
                        :houseNumber.sync="houseNumber"
                        :postCode.sync="postCode"
                        :country.sync="country"
                    ></vuetify-google-autocomplete>
                </v-flex>

                <v-flex xs6 class="pt-0">
                    <p class="subheading ma-0">{{ firstName }} {{ lastName }}</p>
                    <p class="subheading ma-0">{{ company }}</p>
                    <p class="subheading ma-0">{{ street }} {{ houseNumber }}</p>
                    <p class="subheading ma-0">{{ postCode }} {{ city }}</p>
                    <p class="subheading ma-0">{{ country | translation }}</p>
                </v-flex>
                <v-flex xs6 class="pt-0 text-xs-right">
                    <a href="#" @click.prevent="showAddressForm"> {{ 'Address not found?' | translation }}? </a>
                </v-flex>

                <v-flex xs12 class="py-0" v-if="showAgb">
                    <v-checkbox
                        style="background-color: #1a873a"
                        class="d-inline-block mt-0"
                        :disabled="isDisabled"
                        v-model="agb"
                        :rules="[rules.agb]"
                        required
                    >
                        <div slot="label">
                            <component
                                :is="{ template: '<span>' + $language.translate('shipping address agb') + '</span>' }"
                            />
                        </div>
                    </v-checkbox>
                </v-flex>

                <address-form-modal
                    v-model="displayAddressForm"
                    :form-class="formClass"
                    :display-incomplete-address-error="displayIncompleteAddressError"
                    :incomplete-address-error="incompleteAddressError"
                    :display-house-number-field="displayHouseNumber"
                    :input-label-outside="inputLabelOutside"
                    :is-disabled="isDisabled"
                    :input-solo="inputSolo"
                    :button-round="buttonRound"
                    :button-depressed="buttonDepressed"
                    :button-large="buttonLarge"
                    @submit="applyAddressForm"
                    :address-fields.sync="addressFields"
                ></address-form-modal>
            </v-layout>
        </v-form>
    </div>
</template>

<script>
    import module from './store/shippingAddress';
    import ShippingAddressRadioGroup from './ShippingAddressRadioGroup';
    import { createNamespacedHelpers, mapState, mapGetters, mapActions } from 'vuex';
    import Vue from 'vue';
    import VuetifyGoogleAutocomplete from '@breakmedia/vuetify-google-autocomplete';
    import AddressFormModal from '@breakmedia/prime-vue-checkout/src/AddressStep/AddressFormModal';

    const moduleName = 'ShippingAddressForm';
    const loginModuleName = 'Login';
    const { mapMutations } = createNamespacedHelpers(moduleName);

    if (!(Vue.$store.state[moduleName])) {
        Vue.$store.registerModule(moduleName, module);
    }

    export default {
        name: moduleName,
        components: {
            ShippingAddressRadioGroup,
            AddressFormModal,
        },
        created() {
            Vue.use(VuetifyGoogleAutocomplete, {
                apiKey: Vue.$config.VUE_APP_GOOGLE_MAPS_PLACES_API_KEY,
            });

            if (this.preventLoad === false) {
                this.loadAddress();
            }
        },
        props: {
            disabled: {
                type: Boolean,
                default: false,
            },
            preventLoad: {
                type: Boolean,
                default: false,
            },
            displayList: {
                type: Boolean,
                default: false,
            },
            inputSolo: Boolean,
            inputLabelOutside: Boolean,
            buttonDepressed: Boolean,
            buttonRound: Boolean,
            buttonLarge: Boolean,
            formClass: String,
            houseNumberRequired: Boolean,
            showAgb: Boolean,
        },
        data() {
            return {
                agb: false,
                displayAddressForm: false,
                rules: {
                    required: value => !!value || 'Dieses Feld wird benötigt und darf nicht leer sein.',
                    firstName: value =>
                        value
                            ? new RegExp('[a-zA-Z]{2}', 'i').test(value) || 'Das Vorname muss mindestens 2 Zeichen haben.'
                            : '',
                    agb: value => !!value || 'Address confirmation is required',
                },
                addressErrors: [],
                incompleteAddressError: 'The found address is not complete. Please fill the missing data manually.',
                displayIncompleteAddressError: false,
            };
        },
        computed: {
            ...mapState(moduleName, {
                loading: state => state.loading,
                addresses: state => state.addresses,
            }),
            ...mapGetters(loginModuleName, [
                'isUserLoggedIn',
            ]),
            isDisabled() {
                return this.disabled || this.loading;
            },
            firstName: {
                get() {
                    return this.getFirstName();
                },
                set(value) {
                    this.setFirstName(value);
                },
            },
            lastName: {
                get() {
                    return this.getLastName();
                },
                set(value) {
                    this.setLastName(value);
                },
            },
            company: {
                get() {
                    return this.getCompany();
                },
                set(value) {
                    this.setCompany(value);
                },
            },
            street: {
                get() {
                    return this.getStreet();
                },
                set(value) {
                    this.setStreet(value);
                },
            },
            postCode: {
                get() {
                    return this.getPostCode();
                },
                set(value) {
                    this.setPostCode(value);
                },
            },
            city: {
                get() {
                    return this.getCity();
                },
                set(value) {
                    this.setCity(value);
                },
            },
            country: {
                get() {
                    return this.$country.getCountryNameBy('iso_2', this.getCountry());
                },
                set(value) {
                    this.setCountry(value);
                },
            },
            houseNumber: {
                get() {
                    return this.getHouseNumber();
                },
                set(value) {
                    this.setHouseNumber(value);
                },
            },
            displayHouseNumber() {
                return this.houseNumber || this.houseNumberRequired;
            },
            addressFields: {
                get() {
                    let addressFields = {
                        street: this.street,
                        city: this.city,
                        postCode: this.postCode,
                        country: this.getCountry(),
                    };

                    if (this.displayHouseNumber) {
                        addressFields.houseNumber = this.houseNumber;
                    }

                    return addressFields;
                },
                set(value) {
                    Object.keys(value).forEach(key => {
                        this[key] = value[key];
                    });
                },
            },
        },
        methods: {
            ...mapGetters(moduleName, [
                'getFirstName',
                'getLastName',
                'getCompany',
                'getStreet',
                'getPostCode',
                'getCity',
                'getCountry',
                'getHouseNumber',
            ]),
            ...mapMutations([
                'setFirstName',
                'setLastName',
                'setCompany',
                'setStreet',
                'setPostCode',
                'setCity',
                'setCountry',
                'setAddress',
                'resetAddress',
                'setHouseNumber',
            ]),
            ...mapActions(moduleName, {
                loadAddressAction: 'loadAddress',
                saveAddressAction: 'saveAddress',
            }),
            loadAddress() {
                this.loadAddressAction().then(result => {
                    this.$emit('addressLoaded', result);
                });
            },
            resetForm() {
                this.resetAddress();
                this.$refs.form.resetValidation();
            },
            saveAddress() {
                return this.saveAddressAction();
            },
            onAutocompleteChanged() {
                if (this.isAddressValid() !== true) {
                    this.showAddressForm(true);
                }
            },
            validate() {
                let addressValid = this.validateAddress();
                let valid = addressValid && this.$refs.form.validate();

                if (valid) {
                    this.displayIncompleteAddressError = false;
                }

                return valid;
            },
            showAddressForm(displayIncompleteAddressError = false) {
                if (!this.isDisabled) {
                    this.displayIncompleteAddressError =
                        typeof displayIncompleteAddressError === 'boolean' ? displayIncompleteAddressError : false;
                    this.displayAddressForm = true;
                }
            },
            applyAddressForm() {
                this.displayAddressForm = false;
                this.validate();
                this.$emit('submit');
            },
            validateAddress() {
                let addressValid = this.isAddressValid();
                if (addressValid !== true) {
                    this.addressErrors = [addressValid];
                    return false;
                } else {
                    this.addressErrors = [];
                    return true;
                }
            },
            isAddressValid() {
                if (!this.street && !this.postCode && !this.city) {
                    return 'You need to fill the address';
                } else if (!this.street) {
                    return 'Street is missing';
                } else if (!this.postCode) {
                    return 'Post code is missing';
                } else if (!this.city) {
                    return 'City is missing';
                } else if (!this.country) {
                    return 'Country is missing';
                } else if (this.displayHouseNumber && !this.houseNumber) {
                    return 'House number is missing';
                }

                return true;
            },
        },
        watch: {
            isUserLoggedIn(value) {
                if (value) {
                    this.loadAddress();
                } else {
                    this.resetForm();
                }
            },
            street(val) {
                if (!this.displayHouseNumber && this.houseNumber && val) {
                    this.street = val + this.houseNumber;
                    this.houseNumber = null;
                }
            },
            houseNumber(val) {
                if (!this.displayHouseNumber && this.street && val) {
                    this.street += val;
                    this.houseNumber = null;
                }
            },
        },
    };
</script>
